const browser = typeof window !== 'undefined';
import { onMount, onDestroy } from 'svelte';
import { navigate as goto } from 'svelte-routing';

/**
 * A hook to handle error pages and prevent automatic navigation
 * @param {string} errorType - The type of error page ('unauthorized' or '404')
 * @returns {Function} - A cleanup function
 */
export function useErrorPage(errorType) {
  // Only run on the client
  if (!browser) return () => {};
  
  let originalPushState;
  let originalReplaceState;
  let navigationBlocker;
  let blockingTimer;
  let blockCount = 0;
  const MAX_BLOCKS = 5; // Maximum number of navigation blocks to prevent infinite loops
  
  const cleanup = () => {
    // Restore original history methods
    if (originalPushState) {
      history.pushState = originalPushState;
    }
    if (originalReplaceState) {
      history.replaceState = originalReplaceState;
    }
    
    // Remove event listeners
    if (navigationBlocker) {
      window.removeEventListener('popstate', navigationBlocker);
    }
    
    // Clear any timers
    if (blockingTimer) {
      clearTimeout(blockingTimer);
    }
    
    // Remove unload handler
    window.onbeforeunload = null;
  };
  
  onMount(() => {
    // Store original methods
    originalPushState = history.pushState;
    originalReplaceState = history.replaceState;
    
    // Set a timeout to automatically disable blocking after 10 seconds
    blockingTimer = setTimeout(() => {
      console.log('Navigation blocking timeout reached, disabling blocks');
      cleanup();
    }, 10000);
    
    // Block navigation attempts
    history.pushState = function(state, title, url) {
      // Check if we've reached the maximum number of blocks
      if (blockCount >= MAX_BLOCKS) {
        console.log('Maximum navigation blocks reached, allowing navigation');
        return originalPushState.call(this, state, title, url);
      }
      
      blockCount++;
      console.log(`Navigation blocked to: ${url} (Block ${blockCount}/${MAX_BLOCKS})`);
      
      // Allow navigation to the current error page or to dashboard
      if (url.includes(errorType) || url.includes('/dashboard')) {
        return originalPushState.call(this, state, title, url);
      }
      return false;
    };
    
    history.replaceState = function(state, title, url) {
      // Check if we've reached the maximum number of blocks
      if (blockCount >= MAX_BLOCKS) {
        console.log('Maximum navigation blocks reached, allowing replace state');
        return originalReplaceState.call(this, state, title, url);
      }
      
      blockCount++;
      console.log(`Replace state blocked to: ${url} (Block ${blockCount}/${MAX_BLOCKS})`);
      
      // Allow navigation to the current error page or to dashboard
      if (url.includes(errorType) || url.includes('/dashboard')) {
        return originalReplaceState.call(this, state, title, url);
      }
      return false;
    };
    
    // Block popstate navigation
    navigationBlocker = (event) => {
      // Check if we've reached the maximum number of blocks
      if (blockCount >= MAX_BLOCKS) {
        console.log('Maximum navigation blocks reached, allowing popstate');
        return true;
      }
      
      blockCount++;
      console.log(`Popstate blocked (Block ${blockCount}/${MAX_BLOCKS})`);
      event.preventDefault();
      return false;
    };
    window.addEventListener('popstate', navigationBlocker);
    
    // Log for debugging
    console.log(`Error page (${errorType}) hooks installed with a 10-second timeout`);
    
    // Return the cleanup function
    return cleanup;
  });
  
  // Clean up on component destruction
  onDestroy(cleanup);
  
  return cleanup;
} 